import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IAdminTag } from '@interfaces/IAdminTag';
import { SortIcon } from '@/utils/icons';
import {
  getBatteryLevelNumber,
  getCertificateSortOrder,
  getDaysAgo,
  getLinkedSortIndex,
  getLinkedToLabel,
  getStatusSortOrder,
} from '../utils/tagFilters';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { TagRow } from './TagRow';
import styles from '../../adminTable.module.css';

type SortKey = 'tagId' | 'linkedTo' | 'fullName' | 'idNumber' | 'certificates' | 'status' | 'lastSeen' | 'battery';
type SortDir = 'asc' | 'desc';

type SortableColumn = {
  key: SortKey;
  labelKey: string;
  sortable: true;
  center?: boolean;
  width?: 'narrow' | 'medium';
};
type StaticColumn = {
  key: 'phone' | 'actions';
  labelKey: string;
  sortable: false;
  center?: boolean;
  width?: 'narrow' | 'medium';
};
type IColumn = SortableColumn | StaticColumn;

const COLUMNS: IColumn[] = [
  { key: 'tagId', labelKey: 'adminTags.col.tagId', sortable: true },
  { key: 'linkedTo', labelKey: 'adminTags.col.linkedTo', sortable: true, width: 'medium' },
  { key: 'fullName', labelKey: 'adminTags.col.fullName', sortable: true },
  { key: 'idNumber', labelKey: 'adminTags.col.idNumber', sortable: true },
  { key: 'phone', labelKey: 'adminTags.col.phone', sortable: false },
  { key: 'certificates', labelKey: 'adminTags.col.certificates', sortable: true, center: true },
  { key: 'status', labelKey: 'adminTags.col.status', sortable: true, center: true, width: 'narrow' },
  { key: 'lastSeen', labelKey: 'adminTags.col.lastSeen', sortable: true, center: true, width: 'narrow' },
  { key: 'battery', labelKey: 'adminTags.col.battery', sortable: true, center: true, width: 'narrow' },
  { key: 'actions', labelKey: 'adminTags.col.actions', sortable: false, center: true, width: 'narrow' },
];

function compare(a: IAdminTag, b: IAdminTag, key: SortKey): number {
  switch (key) {
    case 'tagId':
      return (a.tagNick ?? a.tagId).localeCompare(b.tagNick ?? b.tagId, undefined, { numeric: true });
    case 'linkedTo': {
      const idx = getLinkedSortIndex(a) - getLinkedSortIndex(b);
      return idx !== 0 ? idx : getLinkedToLabel(a).localeCompare(getLinkedToLabel(b));
    }
    case 'fullName':
      return a.fullName.localeCompare(b.fullName);
    case 'idNumber':
      return (a.idNumber ?? '').localeCompare(b.idNumber ?? '', undefined, { numeric: true });
    case 'certificates':
      return getCertificateSortOrder(a) - getCertificateSortOrder(b);
    case 'status':
      return getStatusSortOrder(a) - getStatusSortOrder(b);
    case 'lastSeen':
      return getDaysAgo(a.lastSeen) - getDaysAgo(b.lastSeen);
    case 'battery':
      return getBatteryLevelNumber(a.batteryLevel) - getBatteryLevelNumber(b.batteryLevel);
    default:
      return 0;
  }
}

interface ITagsTableProps {
  tags: IAdminTag[];
  emptyMessage: string;
  onAssign: (tag: IAdminTag) => void;
  onEdit: (tag: IAdminTag) => void;
  onUnassign: (tag: IAdminTag) => void;
}

export function TagsTable({ tags, emptyMessage, onAssign, onEdit, onUnassign }: ITagsTableProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('linkedTo');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sorted = useMemo(() => {
    const copy = [...tags];
    copy.sort((a, b) => {
      const c = compare(a, b, sortKey);
      return sortDir === 'asc' ? c : -c;
    });
    return copy;
  }, [tags, sortKey, sortDir]);

  return (
    <ScrollArea orientation="both" className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr className={styles.headerRow}>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`${styles.th} ${col.center ? styles.thCenter : ''} ${
                  col.width === 'narrow' ? styles.thNarrow : col.width === 'medium' ? styles.thMedium : ''
                }`}
              >
                {col.sortable ? (
                  <button type="button" className={styles.sortBtn} onClick={() => handleSort(col.key)}>
                    {t(col.labelKey)}
                    <SortIcon />
                  </button>
                ) : (
                  t(col.labelKey)
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((tag) => (
            <TagRow key={tag.tagId} tag={tag} onAssign={onAssign} onEdit={onEdit} onUnassign={onUnassign} />
          ))}
        </tbody>
      </table>
      {sorted.length === 0 && <div className={styles.empty}>{emptyMessage}</div>}
    </ScrollArea>
  );
}
